import React, { useState } from 'react'
import { Upload, FileText, Plus, X, Settings, Sliders, File, Trash2 } from 'lucide-react'

const DEFAULT_PROMPT_TEMPLATES = (base) => [
  `Provide a concise professional summary of "${base}" in 3-4 sentences.`,
  `List 5 key definitions or terms from "${base}" that a practitioner should know.`,
  `Summarize compliance obligations and penalties related to "${base}".`,
  `Which sections would be most relevant for banking or financial institutions in "${base}"?`,
  `Give an example citation and the recommended way to reference "${base}" in a report.`
]

const UploadDocument = ({ onFileUpload }) => {
  const [fileName, setFileName] = useState('')
  const [author, setAuthor] = useState('')
  const [year, setYear] = useState('')
  const [jurisdiction, setJurisdiction] = useState('')
  const [tone, setTone] = useState('Formal')
  const [replyLength, setReplyLength] = useState('Medium')
  const [prompts, setPrompts] = useState([])
  const [newPrompt, setNewPrompt] = useState('')
  const [isDragging, setIsDragging] = useState(false)

  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0]
    if (!file) return
    processFile(file)
  }

  const processFile = (file) => {
    const name = file.name.replace(/\.[^/.]+$/, '') // remove extension
    setFileName(name)
    const defaultPrompts = DEFAULT_PROMPT_TEMPLATES(name)
    setPrompts(defaultPrompts)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = () => {
    setIsDragging(false)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setIsDragging(false)
    const file = e.dataTransfer.files && e.dataTransfer.files[0]
    if (file && file.type === 'application/pdf') {
      processFile(file)
    } else if (file) {
      alert('Please upload a PDF file.')
    }
  }

  const handleAddPrompt = () => {
    if (!newPrompt.trim()) return
    setPrompts(prev => [...prev, newPrompt.trim()])
    setNewPrompt('')
  }

  const handleRemovePrompt = (idx) => {
    setPrompts(prev => prev.filter((_, i) => i !== idx))
  }

  const handleChangePrompt = (idx, value) => {
    setPrompts(prev => prev.map((p, i) => i === idx ? value : p))
  }

  const generateMockSummary = ({ title, metadata, prompts }) => {
    const base = title || 'the document'
    const lower = base.toLowerCase()
    let complexity = 'Medium'
    let risk = 'Moderate'
    if (lower.includes('bank') || lower.includes('financial') || lower.includes('insurance')) {
      complexity = 'High'
      risk = 'High'
    } else if (lower.includes('bylaw') || lower.includes('procedures') || lower.includes('regulations')) {
      complexity = 'Medium'
      risk = 'Moderate'
    } else if (lower.length < 8) {
      complexity = 'Low'
      risk = 'Low'
    }

    const contextParts = []
    if (metadata?.author) contextParts.push(metadata.author)
    if (metadata?.year) contextParts.push(metadata.year)
    if (metadata?.jurisdiction) contextParts.push(metadata.jurisdiction)
    const contextSuffix = contextParts.length ? ` (${contextParts.join(' · ')})` : ''

    const bullets = []
    bullets.push(`Scope & purpose: ${base} sets out the principal objectives and the entities it covers${contextSuffix}.`)
    bullets.push(`Definitions & structure: Key definitions are provided early; the document organises content by sections covering governance, obligations, and sanctions.`)
    if (complexity === 'High') bullets.push('Operational impact: Multiple provisions impose strict compliance and reporting requirements for regulated institutions.')
    else bullets.push('Operational impact: Outlines obligations and administrative procedures for affected parties.')
    bullets.push('Enforcement & penalties: Specifies a range of administrative and potential criminal penalties for breaches.')
    if (prompts && prompts.length > 0) bullets.push(`Useful reading: start with sections referenced by the suggested prompts, for actionable guidance.`)

    const keyClauses = [
      'Short title and commencement',
      'Definitions and interpretation',
      'Registration and licensing requirements',
      'Compliance, reporting, and penalties'
    ]

    const riskAssessment = (() => {
      if (risk === 'High') return 'High risk for regulated parties if controls are not updated—likely financial and reputational consequences.'
      if (risk === 'Low') return 'Low systemic risk; primarily procedural obligations.'
      return 'Moderate risk: non-compliance may lead to administrative penalties and operational disruptions.'
    })()

    return {
      bullets: bullets.slice(0, 5),
      keyClauses,
      riskAssessment,
      complexityRating: complexity,
    }
  }

  const handleUseMock = () => {
    if (!fileName) return alert('Please choose a PDF file to mock.')

    const payload = {
      fileName,
      preconstructedReplies: prompts,
      metadata: {
        author: author || null,
        year: year || null,
        jurisdiction: jurisdiction || null,
      },
      tone,
      replyLength,
      summary: generateMockSummary({ title: fileName, metadata: { author, year, jurisdiction }, prompts }),
    }

    onFileUpload(payload)
  }

  return (
    <div className="w-full bg-white shadow-xl rounded-2xl overflow-hidden border border-gray-100">
      <div className="p-6 border-b border-gray-100 bg-gray-50/50">
        <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <div className="p-2 bg-blue-100 text-blue-600 rounded-lg">
            <Upload size={20} />
          </div>
          Upload Document
        </h2>
        <p className="text-sm text-gray-500 mt-1 ml-11">
          Upload a PDF to generate a professional mock analysis context.
        </p>
      </div>

      <div className="p-6 space-y-6">
        {/* File Drop Zone */}
        <div
          className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all ${isDragging
              ? 'border-blue-500 bg-blue-50'
              : fileName
                ? 'border-green-200 bg-green-50/30'
                : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
            }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <input
            type="file"
            accept=".pdf"
            onChange={handleFileChange}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          />
          {fileName ? (
            <div className="flex flex-col items-center text-green-700">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-3">
                <FileText size={24} />
              </div>
              <div className="font-semibold text-lg">{fileName}</div>
              <div className="text-sm opacity-80">Ready to analyze</div>
            </div>
          ) : (
            <div className="flex flex-col items-center text-gray-500">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                <Upload size={24} />
              </div>
              <div className="font-medium">Click to upload or drag and drop</div>
              <div className="text-xs mt-1">PDF files only</div>
            </div>
          )}
        </div>

        {/* Metadata & Settings */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
              <Settings size={16} className="text-gray-400" />
              Metadata
            </div>
            <div className="space-y-3">
              <input
                value={author}
                onChange={(e) => setAuthor(e.target.value)}
                placeholder="Author (optional)"
                className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
              />
              <div className="grid grid-cols-2 gap-3">
                <input
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  placeholder="Year"
                  className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                />
                <input
                  value={jurisdiction}
                  onChange={(e) => setJurisdiction(e.target.value)}
                  placeholder="Jurisdiction"
                  className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                />
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
              <Sliders size={16} className="text-gray-400" />
              Response Settings
            </div>
            <div className="space-y-3">
              <select
                value={tone}
                onChange={(e) => setTone(e.target.value)}
                className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
              >
                <option>Formal</option>
                <option>Neutral</option>
                <option>Concise</option>
                <option>Detailed</option>
              </select>
              <select
                value={replyLength}
                onChange={(e) => setReplyLength(e.target.value)}
                className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
              >
                <option>Short</option>
                <option>Medium</option>
                <option>Long</option>
              </select>
            </div>
          </div>
        </div>

        {/* Prompts */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-sm font-semibold text-gray-900">Preconstructed Prompts</label>
            <span className="text-xs text-gray-500">{prompts.length} active</span>
          </div>

          <div className="bg-gray-50 rounded-xl p-3 space-y-2 max-h-48 overflow-y-auto border border-gray-200">
            {prompts.map((p, i) => (
              <div key={i} className="flex gap-2 group">
                <input
                  value={p}
                  onChange={(e) => handleChangePrompt(i, e.target.value)}
                  className="flex-1 p-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                />
                <button
                  onClick={() => handleRemovePrompt(i)}
                  className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all opacity-0 group-hover:opacity-100"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
            {prompts.length === 0 && (
              <div className="text-center py-4 text-gray-400 text-sm italic">No prompts added</div>
            )}
          </div>

          <div className="flex gap-2">
            <input
              value={newPrompt}
              onChange={(e) => setNewPrompt(e.target.value)}
              placeholder="Add a custom prompt..."
              className="flex-1 p-2.5 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
              onKeyDown={(e) => e.key === 'Enter' && handleAddPrompt()}
            />
            <button
              onClick={handleAddPrompt}
              disabled={!newPrompt.trim()}
              className="p-2.5 bg-gray-900 text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              <Plus size={20} />
            </button>
          </div>
        </div>
      </div>

      <div className="p-6 border-t border-gray-100 bg-gray-50/50 flex justify-end gap-3">
        <button
          onClick={() => { setFileName(''); setPrompts([]) }}
          className="px-5 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-xl transition-all"
        >
          Reset
        </button>
        <button
          onClick={handleUseMock}
          disabled={!fileName}
          className="px-6 py-2.5 text-sm font-medium bg-blue-600 text-white rounded-xl hover:bg-blue-700 shadow-lg shadow-blue-200 disabled:opacity-50 disabled:shadow-none disabled:cursor-not-allowed transition-all transform active:scale-95"
        >
          Analyze Document
        </button>
      </div>
    </div>
  )
}

export default UploadDocument
